import { pageLabelForPath } from "../../layout/Sidebar";
import type { PageView } from "@/stores/uiContextStore";

/**
 * A name for a view that renders no heading of its own.
 *
 * <h2>The last resort of the three, not the first</h2>
 * A page that renders a title publishes it (`uiContextStore`'s `pageTitle`), and that always wins:
 * a copy of a heading kept anywhere but beside the heading drifts from it, which is how this panel
 * came to announce "Documents" over a tab labelled "Governance Documents", and how it announced
 * "Dashboard" over one whose title is computed per role. Nothing in this table may duplicate a string
 * a page renders.
 *
 * <h2>Why the route is not enough either</h2>
 * `pageLabelForPath` answers with the sidebar's own word for a path, which is what keeps the assistant
 * and the menu from disagreeing. But the catalog list's four type tabs share one route, and neither the
 * tab nor the page renders "Regulatory Catalogs" as a heading — so for those the name exists nowhere
 * else and belongs here.
 *
 * The plain `*_LIST` and `*_DETAIL` keys are deliberately absent: the sidebar's label is already right
 * for them, and repeating it here would be a second place to keep in step with the menu.
 */
const LABEL_BY_VIEW: Partial<Record<PageView, string>> = {
  // The catalog type tabs. Four filters, one route, one screen — and the tab reads "Regulatory", which
  // is not a page name, so this is composed rather than copied.
  CATALOG_LIST_REGULATORY: "Regulatory Catalogs",
  CATALOG_LIST_TECHNOLOGY: "Technology Catalogs",
  CATALOG_LIST_INTEGRATION: "Integration Catalogs",

  // Parts of a record rather than the record: a group or a control is not "the Catalog".
  CONTROL_GROUP_DETAIL: "Control Group",
  CONTROL_DETAIL: "Control",
  SSP_COMPONENT_DETAIL: "SSP Component",
  COMPONENT_ITEM_DETAIL: "Component Item",

  // Authoring surfaces, which cover their list page rather than routing anywhere.
  CATALOG_CREATE: "New Catalog",
  PROFILE_BUILDER: "Profile Builder",

  // Governance's tabs. Nothing publishes these yet; when a page does, it should publish its heading and
  // these should go, as the Insights and Content Registry entries did.
  POLICIES: "Policies",
  WORKFLOWS: "Workflows",
  DELEGATION: "Delegation",
  CHANGES: "Change Requests",
};

/**
 * What the panel calls the page its suggestions were built for.
 *
 * Three sources, most authoritative first: what the page itself said it is called, then this file's
 * name for the view, then the sidebar's label for the route.
 *
 * All three arguments describe the suggestions **on screen**, not the page the user is on now — the two
 * diverge while a request is in flight, and naming the destination above the previous page's questions
 * would be the one thing this line must never do.
 *
 * @returns null when none of them can name the page, which is how the panel drops the line entirely
 *          rather than printing "Page detected: null"
 */
export function panelPageLabel(
  title: string | null | undefined,
  view: PageView | string | null | undefined,
  route: string | null | undefined,
): string | null {
  const published = title?.trim();
  if (published) return published;
  const named = view ? LABEL_BY_VIEW[view as PageView] : undefined;
  return named ?? pageLabelForPath(route);
}
